import type { MyCareerVacancyFeedQuery } from 'core/graphql';

import type { ICareerVacancyDirection, VacancyInteraction } from './transformCareerVacancies';

type FeedNode = NonNullable<MyCareerVacancyFeedQuery['careerVacancyFeed']>['edges'][number]['node'];

export interface IFeedSkill {
  id: string;
  name: string;
  /** Слаг справочника — по нему `SkillIcon` подбирает знак технологии. */
  slug: string;
  category: string;
  iconUrl: string | null;
  /** The student's profile asks for this one — drives the accent chip. */
  matched: boolean;
}

/**
 * One row of the ranked feed (spec 0035).
 *
 * Differs from `ICareerVacancy` in three ways that matter:
 * `descriptionShort` instead of the full text, skills carried as `{id, name}`
 * so a chip can BE a filter, and the match signals the card renders.
 */
export interface IFeedVacancy {
  id: string;
  title: string;
  companyName: string;
  applyUrl: string;
  location: string;
  cityId: string;
  cityName: string;
  workMode: string;
  seniority: string;
  employmentType: string;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string;
  /** Server-side teaser; the full text lives on the singular query. */
  descriptionShort: string;
  publishedAt: string;
  expiresAt: string;
  /** Server clock, not the browser's — a stale tab must not keep calling a
   *  three-week-old vacancy new. */
  isNew: boolean;
  directions: ICareerVacancyDirection[];
  skills: IFeedSkill[];
  matchedSkillCount: number;
  interaction: VacancyInteraction;
}

const toInteraction = (value?: string | null): VacancyInteraction =>
  value === 'saved' || value === 'applied' ? value : null;

/** Zip parallel id/title arrays, dropping any id without a matching title. */
const zip = (ids: string[], titles: string[]): ICareerVacancyDirection[] =>
  ids
    .map((id, index) => ({ id, title: titles[index] }))
    .filter((row): row is ICareerVacancyDirection => Boolean(row.title));

export const transformFeedVacancy = (node: FeedNode): IFeedVacancy => {
  const skillIds = node.skillIds ?? [];
  const skillNames = node.skillNames ?? [];
  const matched = new Set(node.matchedSkillNames ?? []);

  // Слаг и иконку берём из объектного списка `skills`, а совпадение — из
  // плоского: на нём стоит подсветка, и переезжать ей некуда. Сопоставляем по
  // id, а не по индексу: порядок двух списков сервер совпадающим не обещает.
  const detail = new Map((node.skills ?? []).map((row) => [row.id, row]));

  const skills: IFeedSkill[] = skillIds
    .map((id, index) => ({
      id,
      name: skillNames[index],
      slug: detail.get(id)?.slug ?? '',
      category: detail.get(id)?.category ?? '',
      iconUrl: detail.get(id)?.iconUrl ?? null,
      matched: matched.has(skillNames[index]),
    }))
    .filter((skill) => Boolean(skill.name))
    // Matched skills first so a match is never hidden behind «и ещё 4» when
    // the card clips the chip list.
    .sort((a, b) => Number(b.matched) - Number(a.matched));

  return {
    id: node.id,
    title: node.title,
    companyName: node.companyName ?? '',
    applyUrl: node.applyUrl,
    location: node.location ?? '',
    cityId: node.cityId ?? '',
    cityName: node.cityName ?? '',
    workMode: node.workMode ?? '',
    seniority: node.seniority ?? '',
    employmentType: node.employmentType ?? '',
    salaryMin: node.salaryMin ?? null,
    salaryMax: node.salaryMax ?? null,
    salaryCurrency: node.salaryCurrency ?? '',
    descriptionShort: node.descriptionShort ?? '',
    publishedAt: node.publishedAt ?? '',
    expiresAt: node.expiresAt ?? '',
    isNew: Boolean(node.isNew),
    directions: zip(node.directionIds ?? [], node.directionTitles ?? []),
    skills,
    matchedSkillCount: node.matchedSkillCount ?? 0,
    interaction: toInteraction(node.myInteraction),
  };
};
